import { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import { services } from '../../services';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface TickerItem {
  symbol: string;
  name: string;
  price: number;
  change: number;
}

interface LiveTickerProps {
  className?: string;
}

export function LiveTicker({ className }: LiveTickerProps) {
  const [tickerData, setTickerData] = useState<TickerItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTickerData();
    // Set up polling every 30 seconds for live updates
    const interval = setInterval(fetchTickerData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchTickerData = async () => {
    try {
      setLoading(true);
      const response = await services.get<{
        status: boolean;
        message?: string;
        data: TickerItem[];
      }>('/admin/dashboard/live-ticker');

      if (response.status === 200 && response.data?.status && response.data?.data) {
        setTickerData(response.data.data);
      } else if (response.status === 401) {
        console.error('Authentication required for live ticker');
        setTickerData([]);
      } else if (response.status === 403) {
        console.error('Admin access required for live ticker');
        setTickerData([]);
      } else {
        setTickerData([]);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required for live ticker');
        setTickerData([]);
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required for live ticker');
        setTickerData([]);
      } else {
        console.error('Error fetching live ticker:', error);
        setTickerData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const displayData = tickerData.length > 0 ? tickerData : [];

  return (
    <div className={cn('glass-card rounded-xl overflow-hidden', className)}>
      <div className='flex items-center gap-2 px-4 py-2 bg-muted/30 border-b border-border'>
        <span className='w-2 h-2 rounded-full bg-success animate-pulse' />
        <span className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
          Token Prices • Live
        </span>
      </div>
      {loading && displayData.length === 0 ? (
        <div className='flex items-center justify-center py-8 text-muted-foreground'>
          <p>Loading ticker data...</p>
        </div>
      ) : displayData.length === 0 ? (
        <div className='flex items-center justify-center py-8 text-muted-foreground'>
          <p>No ticker data available</p>
        </div>
      ) : (
        <div className='flex overflow-x-auto scrollbar-none'>
          <div className='flex animate-scroll'>
            {[...displayData, ...displayData].map((item, index) => (
              <div
                key={`${item.symbol}-${index}`}
                className='flex items-center gap-3 px-5 py-3 border-r border-border/50 min-w-max'
              >
                <code className='text-sm font-mono font-semibold text-primary'>${item.symbol}</code>
                <span className='text-sm text-foreground font-medium'>
                  ${item.price.toFixed(2)}
                </span>
                <div
                  className={cn(
                    'flex items-center gap-1 text-xs font-medium',
                    item.change >= 0 ? 'text-success' : 'text-destructive'
                  )}
                >
                  {item.change >= 0 ? (
                    <TrendingUp className='w-3 h-3' />
                  ) : (
                    <TrendingDown className='w-3 h-3' />
                  )}
                  {item.change >= 0 ? '+' : ''}
                  {item.change}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
